// The agent's permission-mode selector, for the Dennis composer.
//
// **This is the ONLY place the mode is chosen.** It used to be a cycling chip
// above the project editor's chat composer, which rendered at all 37 `ChatPanel`
// sites — every tile chat included. But the surface where a run is started and
// then watched for minutes is the dashboard thread, and that composer had no mode
// control at all; the editor's chats still ENFORCE the mode (see
// `panels/chatModes.js` and `ChatPanel`'s gate), they just no longer set it.
//
// A menu rather than the old cycling button, and rather than a `<select>`: it can
// carry each mode's one-line `hint` and its colour, and it lets you jump straight
// to Plan instead of pressing through Manual to reach it. `Shift+Tab` still
// cycles — that shortcut is what makes the ORDER in `chatModes.js` a contract with
// `agent_modes.py`, so dropping it would quietly make the mirror pointless.
//
// The label is the whole visual: three short words, each in its mode's colour, and
// no icon. The `▶`/`✋`/`📋` this shipped with were emoji, which the platform draws
// from its own colour font — so CSS `color` cannot touch them (see
// `components/icons.js`) and the glyph fought the per-mode colour rather than
// reinforcing it.
//
// Presentational: the host owns the mode and the persisting, exactly as
// `PromptAttachments` leaves the list and the uploading to its host. It is a
// PROJECT setting, so writing it means a REST call the composer has no business
// making.
//
// Dropdown mechanics are cloned from `PromptAttachments.AttachButton` — the repo's
// idiom for a composer-anchored menu: `aria-haspopup`/`aria-expanded` on the
// trigger, a fixed backdrop div for click-away rather than a document listener,
// and an Escape handler on the wrapper.

import { useState } from 'react';
import { AGENT_MODES, modeMeta, normalizeMode } from '../panels/chatModes';
import './AgentModeMenu.css';

/**
 * @param mode      the current mode; anything unknown reads as `auto`
 * @param onSelect  `(mode) => void` — called only when the choice CHANGES
 * @param disabled  while a send is in flight
 */
export default function AgentModeMenu({ mode, onSelect, disabled }) {
  const [open, setOpen] = useState(false);
  const current = normalizeMode(mode);
  const meta = modeMeta(current);

  function choose(next) {
    setOpen(false);
    // Re-selecting the current mode is not a change. Worth skipping rather than
    // letting it through: `onSelect` posts to the backend and mirrors onto any
    // live run, and `runner.set_mode` treats a same-mode write as a no-op anyway
    // — so sending it would be a request whose only possible outcome is nothing.
    if (next !== current) onSelect?.(next);
  }

  return (
    <div
      className="agent-mode-wrap"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className={`agent-mode-btn agent-mode-${current}`}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        // The hint, not just the label: "Manual" alone does not say what is
        // manual. Shift+Tab is named here because the composer is where it is
        // bound and this chip is what it visibly changes.
        title={`${meta.hint}\n\nShift+Tab cycles`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {meta.label}
      </button>
      {open && (
        <>
          <div className="agent-mode-backdrop" onClick={() => setOpen(false)} />
          {/* Its own accessible name, not the trigger's — two elements sharing
              one name makes the lookup ambiguous while the menu is open. */}
          <div className="agent-mode-menu" role="menu" aria-label="Assistant mode">
            {AGENT_MODES.map((m) => {
              const mm = modeMeta(m);
              return (
                <button
                  key={m}
                  type="button"
                  role="menuitemradio"
                  aria-checked={m === current}
                  className={m === current ? 'agent-mode-item is-current' : 'agent-mode-item'}
                  onClick={() => choose(m)}
                >
                  <span className="agent-mode-item-head">
                    {/* The label carries the mode's colour here too, not just on
                        the trigger — it is the only thing distinguishing the three
                        rows at a glance now that there is no glyph. */}
                    <span className={`agent-mode-item-label agent-mode-text-${m}`}>{mm.label}</span>
                    {/* aria-checked already carries this for a screen reader, so
                        the mark is decorative. */}
                    <span aria-hidden="true" className="agent-mode-item-tick">
                      {m === current ? '✓' : ''}
                    </span>
                  </span>
                  <span className="agent-mode-item-hint">{mm.hint}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
